import React, { useEffect, useState } from 'react';
import {
	getCachedTeamLogoSrc,
	prefetchTeamLogos,
	subscribeTeamLogoCache,
	TEAM_LOGO_CACHE_SIZE
} from '../../utils/teamLogoCache';

const POSITION_COLORS = {
	QB: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:bg-opacity-30 dark:text-blue-200 dark:border-blue-800',
	RB: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:bg-opacity-30 dark:text-green-200 dark:border-green-800',
	WR: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:bg-opacity-30 dark:text-yellow-200 dark:border-yellow-800',
	TE: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900 dark:bg-opacity-30 dark:text-purple-200 dark:border-purple-800'
};

const PLAYOFF_WEEKS = [15, 16, 17];

function TeamLogo({ team, sizeClassName = 'h-8 w-8', size = TEAM_LOGO_CACHE_SIZE }) {
	const imageUrl = team?.image_url;
	const [src, setSrc] = useState(() => getCachedTeamLogoSrc(imageUrl, size));

	useEffect(() => {
		if (!imageUrl) {
			setSrc(null);
			return undefined;
		}

		prefetchTeamLogos([imageUrl], size);
		setSrc(getCachedTeamLogoSrc(imageUrl, size));

		return subscribeTeamLogoCache(() => {
			setSrc(getCachedTeamLogoSrc(imageUrl, size));
		});
	}, [imageUrl, size]);

	if (!src) {
		return <div className={`flex-shrink-0 ${sizeClassName}`} aria-hidden="true" />;
	}

	return (
		<img
			src={src}
			alt={`${team.abbreviation || team.name} logo`}
			className={`flex-shrink-0 object-contain ${sizeClassName}`}
			onError={(event) => {
				event.currentTarget.style.display = 'none';
			}}
		/>
	);
}

function getWeekMatchup(team, week) {
	return team.weeks && (team.weeks[week] || team.weeks[String(week)]);
}

function TeamHeaderCell({ team }) {
	return (
		<div className="flex flex-col items-center justify-center text-center gap-1 h-full px-2 py-3 md:flex-row md:text-left md:gap-3 md:px-3">
			<TeamLogo
				team={team}
				sizeClassName="h-9 w-9 md:h-12 md:w-12"
			/>
			<div className="min-w-0 w-full">
				<div className="text-base md:text-xl font-black tracking-tight text-gray-900 dark:text-dark-text">
					{team.abbreviation}
				</div>
				<div className="hidden md:block text-2xs text-gray-500 dark:text-gray-400 truncate">
					{team.conference} · {team.division}
				</div>
			</div>
		</div>
	);
}

function WeekCellContent({ week, matchup, opponentTeam }) {
	const weekLabel = (
		<div className="whitespace-nowrap text-2xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
			<span className="md:hidden">W{week}</span>
			<span className="hidden md:inline">Week {week}</span>
		</div>
	);

	if (!matchup) {
		return (
			<>
				{weekLabel}
				<div className="mt-1 flex h-8 md:h-9 items-center justify-center whitespace-nowrap text-sm font-semibold text-gray-400">
					Bye
				</div>
			</>
		);
	}

	const venueLabel = matchup.is_home_game ? 'vs' : '@';

	return (
		<>
			{weekLabel}
			<div className="mt-1 flex h-8 md:h-9 flex-col items-center justify-center gap-1 whitespace-nowrap md:flex-row md:gap-2">
				<span className="text-2xs font-bold text-gray-500 dark:text-gray-400 uppercase md:text-xs">
					{venueLabel}
				</span>
				{opponentTeam && (
					<TeamLogo
						team={opponentTeam}
						sizeClassName="h-7 w-7 md:h-8 md:w-8"
					/>
				)}
				<span className="text-sm md:text-base font-bold text-gray-900 dark:text-dark-text">
					{matchup.opponent_abbreviation}
				</span>
			</div>
		</>
	);
}

function WeekCell({ week, matchup, opponentTeam, onWeekClick, interactive, fixedWidth }) {
	const widthClass = fixedWidth ? 'flex-shrink-0 w-32' : '';
	const fixedWidthStyle = fixedWidth
		? { width: '8rem', minWidth: '8rem', maxWidth: '8rem' }
		: undefined;
	const baseClassName =
		`flex flex-col items-center justify-start px-2 py-3 border-l border-gray-200 dark:border-dark-border md:px-3 ${widthClass}`;

	if (!interactive || !matchup || !onWeekClick) {
		return (
			<div className={baseClassName} style={fixedWidthStyle}>
				<WeekCellContent week={week} matchup={matchup} opponentTeam={opponentTeam} />
			</div>
		);
	}

	return (
		<button
			type="button"
			onClick={() => onWeekClick(week, matchup)}
			title={`Compare Week ${week} matchup`}
			className={`${baseClassName} transition-colors hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:bg-opacity-20 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900 dark:focus:bg-opacity-20`}
			style={fixedWidthStyle}
		>
			<WeekCellContent week={week} matchup={matchup} opponentTeam={opponentTeam} />
		</button>
	);
}

function RosterColumn({ team, slotState, isFirst, mobileLabel }) {
	const players = (slotState && slotState.players) || null;
	const loading = Boolean(slotState && slotState.loading);
	const error = (slotState && slotState.error) || '';

	return (
		<div
			className={`px-2 py-3 ${
				isFirst ? '' : 'border-t md:border-t-0 md:border-l border-gray-200 dark:border-dark-border'
			}`}
		>
			{team && !isFirst && (
				<div className="md:hidden flex items-center gap-2 px-1 pb-2 mb-2 border-b border-gray-200 dark:border-dark-border">
					<TeamLogo team={team} sizeClassName="h-7 w-7" />
					<div className="min-w-0">
						{mobileLabel && (
							<div className="text-2xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
								{mobileLabel}
							</div>
						)}
						<div className="text-sm font-bold text-gray-900 dark:text-dark-text truncate">
							{team.abbreviation}
						</div>
					</div>
				</div>
			)}
			{loading && (
				<div className="text-xs text-gray-500 dark:text-gray-400 px-1 py-2">
					Loading roster…
				</div>
			)}
			{!loading && error && (
				<div className="text-xs text-red-600 dark:text-red-400 px-1 py-2">{error}</div>
			)}
			{!loading && !error && players && players.length === 0 && (
				<div className="text-xs text-gray-500 dark:text-gray-400 px-1 py-2">
					No ADP data on file.
				</div>
			)}
			{!loading && players && players.length > 0 && (
				<ul className="space-y-1">
					{players.map((player) => (
						<li
							key={`${player.position}-${player.id}`}
							className="flex items-center justify-between gap-2 text-xs px-1 py-1 rounded hover:bg-gray-50 dark:hover:bg-dark-bg"
						>
							<div className="flex items-center gap-2 min-w-0">
								<span
									className={`inline-flex flex-shrink-0 rounded border px-1 py-0.5 text-2xs font-bold ${
										POSITION_COLORS[player.position] || ''
									}`}
								>
									{player.position}
								</span>
								<span className="truncate text-gray-800 dark:text-dark-text">
									{player.first_name} {player.last_name}
								</span>
							</div>
							<span className="flex-shrink-0 font-semibold text-gray-600 dark:text-gray-300">
								{player.underdog_adp}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export default function TeamChampionshipWeeksCard({
	team,
	teamsById,
	weeks = PLAYOFF_WEEKS,
	isExpanded = false,
	playerSlots = {},
	onToggleExpand,
	onWeekClick,
	interactive = true,
	showExpand = true
}) {
	const weekMatchups = weeks.map((week) => ({
		week,
		matchup: getWeekMatchup(team, week)
	}));
	const isScrollLayout = weeks.length > 3;

	return (
		<div
			id={`championship-team-card-${team.id}`}
			style={{ scrollMarginTop: '1rem' }}
			className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm overflow-hidden"
		>
			{isScrollLayout ? (
				<div className="flex items-stretch overflow-x-auto">
					<div className="sticky left-0 z-10 flex-shrink-0 w-24 md:w-40 bg-white dark:bg-dark-card border-r border-gray-200 dark:border-dark-border">
						<TeamHeaderCell team={team} />
					</div>
					{weekMatchups.map(({ week, matchup }) => (
						<WeekCell
							key={week}
							week={week}
							matchup={matchup}
							opponentTeam={matchup ? teamsById[matchup.opponent_id] : null}
							onWeekClick={onWeekClick}
							interactive={interactive}
							fixedWidth
						/>
					))}
				</div>
			) : (
				<div className="grid grid-cols-4 items-stretch">
					<TeamHeaderCell team={team} />
					{weekMatchups.map(({ week, matchup }) => (
						<WeekCell
							key={week}
							week={week}
							matchup={matchup}
							opponentTeam={matchup ? teamsById[matchup.opponent_id] : null}
							onWeekClick={onWeekClick}
							interactive={interactive}
						/>
					))}
				</div>
			)}

			{showExpand && isExpanded && (
				<div className="grid grid-cols-1 md:grid-cols-4 border-t border-gray-200 dark:border-dark-border">
					<RosterColumn
						team={team}
						slotState={playerSlots[team.id]}
						mobileLabel="Team"
						isFirst
					/>
					{weekMatchups.map(({ week, matchup }) => {
						const opponentTeam = matchup ? teamsById[matchup.opponent_id] : null;
						return (
							<RosterColumn
								key={week}
								team={opponentTeam}
								slotState={opponentTeam ? playerSlots[opponentTeam.id] : null}
								mobileLabel={`Week ${week} opp`}
							/>
						);
					})}
				</div>
			)}

			{showExpand && onToggleExpand && (
				<button
					type="button"
					onClick={() => onToggleExpand(team.id)}
					className="w-full flex items-center justify-center border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800 dark:bg-opacity-30 px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
					aria-expanded={isExpanded}
					aria-label={isExpanded ? 'Hide rosters' : 'Show rosters'}
					title={isExpanded ? 'Hide rosters' : 'Show rosters'}
				>
					<svg
						className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
					</svg>
				</button>
			)}
		</div>
	);
}
